import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { QuizzesAPI, ClassesAPI } from "../utils/api";
import { buildShortId } from "../utils/share";
import { Quiz } from "../types";
import { getToken } from "../utils/auth";
import MemberManagement from "../components/MemberManagement"; // Import the component

const ClassViewPage: React.FC = () => {
  const { classId } = useParams<{ classId: string }>();
  const navigate = useNavigate();
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState("Lớp học");
  const [isOwner, setIsOwner] = useState(false);
  const [activeTab, setActiveTab] = useState<'quizzes' | 'members'>('quizzes'); // Add tab state

  useEffect(() => {
    (async () => {
      try {
        const token = getToken();
        if (!token || !classId) {
          setLoading(false);
          return;
        }
        // fetch class by id or by short code
        const mine = await ClassesAPI.listMine(token).catch(() => []);
        const pub = await ClassesAPI.listPublic(token).catch(() => []);
        const all = [...mine, ...pub];
        const cls = all.find(
          (c: any) => c.id === classId || buildShortId(c.id) === classId
        );
        if (cls) setTitle(cls.name);
        const effectiveClassId = cls ? cls.id : classId;
        const qzs = await QuizzesAPI.byClass(effectiveClassId, token);
        const ownerIds = new Set(
          mine
            .filter((m: any) => m.accessType === "owner")
            .map((m: any) => m.id)
        );
        const owner = ownerIds.has(effectiveClassId);



        setIsOwner(owner);
        const visible = owner ? qzs : qzs.filter((q: any) => q.published);
        setQuizzes(visible);
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    })();
  }, [classId]);

  if (loading) {
    const SpinnerLoading = require("../components/SpinnerLoading").default;
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div style={{ transform: 'scale(0.435)' }}>
          <SpinnerLoading />
        </div>
      </div>
    );
  }

  const handleResetCode = async () => {
    if (!window.confirm('Bạn có chắc chắn muốn đổi mã tham gia? Mã cũ sẽ không còn hiệu lực.')) return;
    try {
      const token = getToken();
      if (!token || !classId) return;
      const res = await require("../utils/api").MembersAPI.resetCode(classId, token);
      window.alert(`Mã tham gia mới là: ${res.code}`);
    } catch (e) {
      window.alert('Lỗi đổi mã');
    }
  };

  return (
    <div className="w-full max-w-[1600px] mx-auto p-4 sm:p-6 min-h-screen">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
        <div className="flex flex-col">
          <Link to="/" className="text-sm text-gray-500 dark:text-gray-400 hover:text-blue-500 mb-2 font-medium transition-colors">
            ← Quay lại trang chủ
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
            {title}
          </h1>
        </div>
        {isOwner && activeTab === 'quizzes' && (
          <div className="flex gap-2">
            <button onClick={handleResetCode} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-sm font-medium transition-all">
              🔑 Đổi Mã Tham Gia
            </button>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-8 border-b border-gray-200 dark:border-gray-800 mb-8">
        <button
          className={`pb-4 font-semibold text-sm transition-all border-b-2 ${activeTab === 'quizzes' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
          onClick={() => setActiveTab('quizzes')}
        >
          Bài Kiểm Tra
        </button>
        {isOwner && (
          <button
            className={`pb-4 font-semibold text-sm transition-all border-b-2 ${activeTab === 'members' ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
            onClick={() => setActiveTab('members')}
          >
            Thành viên
          </button>
        )}
      </div>

      {/* Content */}
      <div className="animate-fadeIn">
        {activeTab === 'quizzes' ? (
          quizzes.length === 0 ? (
            <div className="card p-12 text-center text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 border-dashed border-2 border-gray-200 dark:border-gray-700 rounded-xl">
              <p className="text-lg font-medium">Chưa có bài kiểm tra nào.</p>
              <p className="text-sm mt-2 opacity-75">Các bài kiểm tra sẽ xuất hiện ở đây khi được tạo.</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {quizzes.map((q) => (
                <div
                  key={q.id}
                  className="group bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm hover:shadow-md transition-all border border-gray-100 dark:border-gray-700 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-lg text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors truncate">
                      {q.title}
                    </div>
                    {q.description && (
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-2">
                        {q.description}
                      </div>
                    )}
                    {!isOwner && !(q as any).published && (
                      <span className="inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 text-xs rounded-full font-medium border border-amber-100 dark:border-amber-800">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                        Nháp (Chỉ bạn thấy)
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() =>
                      navigate(`/quiz/${q.id}`, { state: { className: title } })
                    }
                    className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 transition-all text-sm whitespace-nowrap"
                  >
                    Làm bài
                  </button>
                </div>
              ))}
            </div>
          )
        ) : (
          isOwner && classId && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <MemberManagement classId={classId} />
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default ClassViewPage;
